import { useEffect, useState } from "react";
import {
  Alert, Badge, Box, Button, ColumnLayout, Container, Form, FormField, Header,
  Input, Select, SpaceBetween, Spinner, StatusIndicator,
} from "@cloudscape-design/components";
import { getAuthToken } from "./auth";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";

interface AccountOption {
  value: string; // accountid
  label: string;
  alias: string;
}

interface SyncResult {
  project: { documentId: string; name: string; id: number };
  action: "created" | "existing";
  stacks: { imported: number; updated: number; errors: number; attached: number };
  workloads: {
    created: number;
    updated: number;
    results: Array<{ type: string; action: "created" | "updated"; name: string }>;
  };
  customProduct: any | null;
  message: string;
}

export default function SyncByIclProjectPage() {
  useDocumentTitle("Sync By ICLProject");
  const [tagValue, setTagValue] = useState("");
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SyncResult | null>(null);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    apiFetch(`/accounts`)
      .then((r) => r.json())
      .then((json) => {
        const opts = (json.data ?? []).map((a: any) => ({
          value: a.accountid,
          label: `${a.Alias} (${a.accountid})`,
          alias: a.Alias,
        }));
        setAccountOptions(opts);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async () => {
    setError(null);
    setResult(null);

    if (!tagValue.trim()) {
      setError("Tag value is required.");
      return;
    }
    if (!selectedAccount) {
      setError("Please select an account.");
      return;
    }

    const creds = getAwsCreds(selectedAccount.alias);
    if (!creds) {
      setError(`No AWS credentials for account "${selectedAccount.alias}". Add credentials in Settings first.`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiFetch(`/projects/sync-from-aws`, {
        method: "POST",
        headers: {
          "x-aws-access-key-id": creds.accessKeyId,
          "x-aws-secret-access-key": creds.secretAccessKey,
          "x-aws-session-token": creds.sessionToken,
        },
        body: JSON.stringify({
          tagValue: tagValue.trim(),
          accountId: selectedAccount.value,
          region: getDefaultRegion(),
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      setResult(await res.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const customProductSummary = (): { label: string; discovered: number } => {
    const cp = result?.customProduct;
    if (cp && cp.product) {
      return { label: cp.product.name, discovered: cp.results?.totalDiscovered ?? 0 };
    }
    return { label: "none found", discovered: 0 };
  };

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description="Reconcile a project and its products from AWS using a single ICLProject tag value."
          >
            Sync By ICLProject
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Alert type="info" header="What this does">
            Finds or creates a project named after the ICLProject tag value, scoped to the selected
            account. It then attaches any discoverable CloudFormation stacks tagged with that
            ICLProject value. If no stacks are found, it discovers AWS resources with that tag and
            creates a custom product from them. The project is always kept, even when no products
            are found.
          </Alert>

          <Form
            actions={
              <Button variant="primary" onClick={handleSubmit} loading={submitting}>
                Sync
              </Button>
            }
          >
            <SpaceBetween size="m">
              <FormField
                label="ICLProject tag value"
                description="The ICLProject AWS tag value to reconcile. Also used as the project name."
              >
                <Input
                  value={tagValue}
                  onChange={({ detail }) => setTagValue(detail.value)}
                  placeholder="e.g. my-product"
                />
              </FormField>

              <FormField
                label="Account"
                description="The AWS account to scope the project to and discover resources in. Uses the credentials stored for this account."
              >
                <Select
                  selectedOption={selectedAccount}
                  onChange={({ detail }) => setSelectedAccount(detail.selectedOption as unknown as AccountOption)}
                  options={accountOptions}
                  placeholder="Select account"
                  filteringType="auto"
                />
              </FormField>
            </SpaceBetween>
          </Form>

          {error && <Alert type="error" header="Sync failed">{error}</Alert>}
          {submitting && (
            <Box textAlign="center">
              <Spinner /> Running discovery…
            </Box>
          )}
        </SpaceBetween>
      </Container>

      {result && (
        <Container header={<Header variant="h2">Result</Header>}>
          <SpaceBetween size="m">
            <StatusIndicator type="success">{result.message}</StatusIndicator>
            <ColumnLayout columns={2} variant="text-grid">
              <div key="project">
                <Box variant="awsui-key-label">Project</Box>
                <Box>
                  {result.project.name}{" "}
                  <Badge color={result.action === "created" ? "green" : "blue"}>{result.action}</Badge>
                </Box>
              </div>
              <div key="stacks">
                <Box variant="awsui-key-label">Stacks attached</Box>
                <Box>
                  {result.stacks.attached} (imported {result.stacks.imported}, updated{" "}
                  {result.stacks.updated}
                  {result.stacks.errors > 0 ? `, errors ${result.stacks.errors}` : ""})
                </Box>
              </div>
              <div key="custom-product">
                <Box variant="awsui-key-label">Custom product</Box>
                <Box>
                  {customProductSummary().label}
                  {customProductSummary().discovered > 0
                    ? ` (${customProductSummary().discovered} resource(s) discovered)`
                    : ""}
                </Box>
              </div>
              <div key="workloads">
                <Box variant="awsui-key-label">Workload products</Box>
                <Box>
                  {result.workloads.created + result.workloads.updated > 0
                    ? `${result.workloads.created} created, ${result.workloads.updated} updated`
                    : "none detected"}
                </Box>
              </div>
            </ColumnLayout>
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
}
